'use client';

import React, { useCallback, useState, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, AlertCircle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FileUploadProps {
  entityType: 'jd' | 'resume';
  entityId?: string;
  accentColor?: string;
  onComplete: (result: {
    storagePath: string;
    parsedText: string;
    candidateName?: string;
    roleTitle?: string;
    confidence?: 'high' | 'medium' | 'low';
    warning?: string | null;
  }) => void;
  onCancel?: () => void;
  autoSave?: boolean;
  signedUrlEndpoint?: string;
  parsePdfEndpoint?: string;
}

type UploadState = 'idle' | 'uploading' | 'parsing' | 'complete' | 'error';

interface UploadError {
  message: string;
  code: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const label = {
  jd: 'Job Description',
  resume: 'Resume',
} as const;

export function FileUpload({
  entityType,
  entityId,
  accentColor,
  onComplete,
  onCancel,
  autoSave = true,
  signedUrlEndpoint = '/api/upload/signed-url',
  parsePdfEndpoint = '/api/upload/parse-pdf',
}: FileUploadProps) {
  const tc = useTranslations('common');
  const [state, setState] = useState<UploadState>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<UploadError | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');

  const entityLabel = label[entityType];

  const reset = useCallback(() => {
    setState('idle');
    setProgress(0);
    setError(null);
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    if (file.type !== 'application/pdf') {
      return { valid: false, error: tc('onlyPdfAllowed') };
    }
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: tc('fileSizeExceeded', { limit: MAX_FILE_SIZE / (1024 * 1024) }),
      };
    }
    return { valid: true };
  };

  const handleFileSelect = useCallback(
    async (file: File) => {
      const validation = validateFile(file);
      if (!validation.valid) {
        setError({ message: validation.error!, code: 'VALIDATION_ERROR' });
        setState('error');
        return;
      }

      setFileName(file.name);
      setState('uploading');
      setProgress(0);
      setError(null);

      try {
        setProgress(10);
        const signedUrlResponse = await fetch(signedUrlEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            entityType,
            entityId,
          }),
        });

        if (!signedUrlResponse.ok) {
          const errorData = await signedUrlResponse.json().catch(() => ({}));
          throw new Error(errorData.error || tc('failedUploadUrl'));
        }

        const { signedUrl, storagePath: path } = await signedUrlResponse.json();
        setProgress(30);

        const uploadResponse = await fetch(signedUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
            'Content-Length': String(file.size),
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(tc('failedUploadStorage'));
        }

        setProgress(60);
        setState('parsing');

        const parseResponse = await fetch(parsePdfEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ storagePath: path, entityType }),
        });

        if (!parseResponse.ok) {
          const errorData = await parseResponse.json().catch(() => ({}));
          throw new Error(errorData.error || tc('failedParsePdf'));
        }

        const {
          text,
          confidence: conf,
          warning: warn,
          candidateName: name,
          roleTitle: role,
        } = await parseResponse.json();
        setProgress(100);

        // Always auto-save — skip the review step.
        onComplete({
          storagePath: path,
          parsedText: text,
          candidateName: name,
          roleTitle: role,
          confidence: conf,
          warning: warn || null,
        });
        setState('complete');
      } catch (err) {
        console.error('Upload error:', err);
        setError({
          message: err instanceof Error ? err.message : tc('uploadUnexpectedError'),
          code: 'UPLOAD_ERROR',
        });
        setState('error');
      }
    },
    [entityId, entityType, onComplete, parsePdfEndpoint, signedUrlEndpoint],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const files = e.dataTransfer.files;
      if (files.length > 0) handleFileSelect(files[0]);
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) handleFileSelect(files[0]);
    },
    [handleFileSelect],
  );

  const containerClass = 'rounded-lg border bg-card text-card-foreground';

  // ── Idle ──────────────────────────────────────────────────────────
  if (state === 'idle') {
    return (
      <div className={containerClass}>
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors rounded-lg"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
          }}
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-muted/30">
            <Upload className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{tc('uploadJd', { label: entityLabel })}</p>
          </div>
          {onCancel ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
            >
              {tc('cancel')}
            </Button>
          ) : null}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={handleInputChange}
          className="hidden"
        />
      </div>
    );
  }

  // ── Uploading ─────────────────────────────────────────────────────
  if (state === 'uploading') {
    return (
      <div className={cn(containerClass, 'px-4 py-3')}>
        <div className="flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">
              {tc('uploading', { file: fileName || entityLabel })}
            </p>
            <Progress value={progress} className="mt-1.5 h-1.5" />
          </div>
        </div>
      </div>
    );
  }

  // ── Parsing ───────────────────────────────────────────────────────
  if (state === 'parsing') {
    return (
      <div className={cn(containerClass, 'px-4 py-3')}>
        <div className="flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
          <p className="text-sm text-muted-foreground">
            {tc('extracting', { file: fileName || entityLabel })}
          </p>
        </div>
      </div>
    );
  }

  // ── Complete ──────────────────────────────────────────────────────
  if (state === 'complete') {
    return (
      <div className={cn(containerClass, 'px-4 py-3')}>
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{tc('uploaded', { label: entityLabel })}</p>
            <p className="text-xs text-muted-foreground truncate">{fileName}</p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={reset}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            {tc('replace')}
          </Button>
        </div>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────
  if (state === 'error') {
    return (
      <div className={containerClass}>
        <Alert variant="destructive" className="border-0 rounded-lg">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-2 w-full">
            <span className="text-sm truncate">{error?.message || tc('uploadFailed')}</span>
            <div className="flex items-center gap-1 shrink-0">
              <Button type="button" variant="outline" size="sm" onClick={reset}>
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                {tc('retry')}
              </Button>
              {onCancel ? (
                <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
                  {tc('cancel')}
                </Button>
              ) : null}
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return null;
}
